import Layout from 'antd/lib/layout/layout';
import PageContainer from 'components/layout/PageContainer';
import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { Redirect, Switch } from 'react-router-dom';
import { dataModules, legacyRoutesMap } from 'routes';
import TrackedRoute from '@tools/ui/components/common/layout/TrackedRoute';
import type { AppRouter, TRoutes } from '@tools/ui/types';
import { ModuleRouter, PageRouter } from '@tools/ui/types';
import useLocalStorage from '@tools/ui-hooks/useLocalStorage';
import useRouter from '@tools/ui-hooks/useRouter.hook';
import { AbilityContext, moduleStore, permissionStore, userStore } from '@tools/ui-stores';
import { UnconfiguredRouteScreen } from '../components/common/unconfigured-route-screen';

const AppContainer: React.FC<React.PropsWithChildren> = observer(() => {
  const userAbility = React.useContext(AbilityContext);
  const moduleRoutesMap = permissionStore.getModuleRoutesMap;
  const { isUserLoggedIn, currentUser } = userStore;

  const { location, replace: replacePath, push: goTo } = useRouter();

  const [isChangedLocation, setIsChangedLocation] = React.useState(false);
  const [isSaveLastLocation] = useLocalStorage('saveLastLocation', true);
  const [locationAfterLogin, setLocationAfterLogin] = useLocalStorage(
    'locationAfterLogin',
    location.pathname,
  );

  const protectedDataModules = React.useMemo(() => {
    const userAbilityRules = userAbility.rules.map((rule) => rule.subject);
    const flat = (arr: (ModuleRouter | PageRouter)[]) => {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < arr.length; i++) {
        if (!arr[i]) {
          arr.splice(i, 1);
          // eslint-disable-next-line no-plusplus
          i--;
        }
        const pages = (arr[i] as ModuleRouter)?.pages;
        if (pages?.length) {
          const updatedPages = flat(pages);
          if (!updatedPages.length && !arr[i].component) {
            arr.splice(i, 1);
            // eslint-disable-next-line no-plusplus
            i--;
          }
        }

        const tabs = (arr[i] as PageRouter)?.tabs;
        if (tabs?.length) {
          const updatedTabs = flat(tabs as unknown as (ModuleRouter | PageRouter)[]);
          if (!updatedTabs.length) {
            arr.splice(i, 1);
            // eslint-disable-next-line no-plusplus
            i--;
          }
        }
      }

      return arr;
    };

    return cloneDeep(dataModules).map((module) => {
      const { name, routes } = module;
      const isAppRouter = Boolean((routes as AppRouter).modules);
      const [defaultRoute, ...moduleRoutes] = isAppRouter
        ? (routes as AppRouter).modules
        : (routes as TRoutes);

      const moduleRoutesPathArr = (
        moduleRoutesMap[name]?.routes?.filter((route) => userAbility.cannot('read', route.path)) ||
        []
      ).map((r) => r.objectPath);
      const removedModuleRoutes = omit(moduleRoutes, moduleRoutesPathArr);
      const preFilteredRoutes = (Object.values(removedModuleRoutes) as ModuleRouter[]).filter(
        (route) => userAbilityRules.some((ability: string) => ability.startsWith(route.path)),
      );
      const filteredModuleRoutes = (preFilteredRoutes as ModuleRouter[]).map((item) => ({
        ...item,
        pages: item?.pages ? flat(item.pages) : undefined,
      }));

      return {
        ...module,
        name,
        routes: isAppRouter
          ? { modules: [defaultRoute, ...filteredModuleRoutes] }
          : [defaultRoute, ...filteredModuleRoutes],
      };
    });
  }, [moduleRoutesMap, userAbility]);

  const allowedDataModules = React.useMemo(() => {
    const userAuthorizedSubRoutes = userAbility.rules.map((rule) => rule.subject) as string[];
    return protectedDataModules.filter((module) => {
      const [, ...routes] = (module.routes as AppRouter).modules || (module.routes as TRoutes);

      return routes.some((route) =>
        userAuthorizedSubRoutes.some((ability) => ability.startsWith(route.path)),
      );
    });
  }, [protectedDataModules, userAbility]);
  const isModuleAllowed = React.useMemo(
    () => allowedDataModules.some((p) => location.pathname.startsWith(p.path)),
    [location, allowedDataModules],
  );

  const isAdmin = React.useMemo(() => {
    return userStore.hasTeamRole({ teamName: 'Tools', roleName: 'Admin' });
  }, []);

  const unconfiguredRouteInfo = React.useMemo(() => {
    const currentPath = location.pathname;

    const currentModule = dataModules.find((module) => currentPath.startsWith(module.path));

    if (!currentModule) {
      return null;
    }

    const configuredModuleFromDb = permissionStore.modules?.find(
      (m) => m.name === currentModule.name,
    );
    const configuredRoutePathsInDb = new Set(
      configuredModuleFromDb?.routes?.map((r) => r.path) || [],
    );
    const routesInCode = moduleRoutesMap[currentModule.name]?.routes || [];

    const unconfiguredRoute = routesInCode.find(
      (route) => currentPath.startsWith(route.path) && !configuredRoutePathsInDb.has(route.path),
    );

    if (unconfiguredRoute) {
      return {
        routePath: unconfiguredRoute.path,
        moduleName: currentModule.name,
      };
    }

    return null;
  }, [location.pathname, moduleRoutesMap]);

  useEffect(() => {
    const path = location.pathname;
    const [legacyRoute, newRoute = ''] =
      Object.entries(legacyRoutesMap).find(([route]) => path.startsWith(route)) || [];

    if (legacyRoute) {
      const currentRoute = `${location.pathname.replace(legacyRoute, newRoute)}${location.search}`;
      replacePath(currentRoute);
      return;
    }

    const corruptedRoute = path.split('//');
    if (corruptedRoute.length > 1) {
      replacePath(corruptedRoute[0]);
    }
  }, [location, replacePath]);

  useEffect(() => {
    moduleStore.setDataModules(location, allowedDataModules, currentUser!.favoriteModules);
  }, [allowedDataModules, location, currentUser]);

  useEffect(() => {
    if (!isUserLoggedIn) {
      return;
    }

    if (!isChangedLocation && isSaveLastLocation) {
      goTo(locationAfterLogin);
      setIsChangedLocation(true);
    }

    setLocationAfterLogin(location.pathname);
  }, [
    goTo,
    isChangedLocation,
    isSaveLastLocation,
    isUserLoggedIn,
    location.pathname,
    locationAfterLogin,
    setLocationAfterLogin,
  ]);

  if (!isModuleAllowed) {
    if (isAdmin && unconfiguredRouteInfo) {
      return (
        <UnconfiguredRouteScreen
          routePath={unconfiguredRouteInfo.routePath}
          moduleName={unconfiguredRouteInfo.moduleName}
        />
      );
    }
    return <Redirect to={allowedDataModules[0]?.path || '/'} />;
  }

  return (
    <Layout>
      <Switch>
        {allowedDataModules.map(({ path, name, routes, extraComponent }) => (
          <TrackedRoute key={name} path={path}>
            <PageContainer router={routes as AppRouter} extraComponent={extraComponent} />
          </TrackedRoute>
        ))}
      </Switch>
    </Layout>
  );
});

export default AppContainer;
